import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import OperatorPageTip from "@/components/OperatorPageTip";
import { AdminAvailabilityOverview } from "@/components/AdminAvailabilityOverview";
import { useCalendarConnections, AppleConnectDialog } from "@/components/CalendarConnections";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CaretLeft, CaretRight, GoogleLogo, AppleLogo, CheckCircle, FloppyDisk, Info, Calendar as CalendarIcon, X } from "@phosphor-icons/react";

// 24 slot da 30 minuti: [{h:8,m:0}, {h:8,m:30}, {h:9,m:0}, ..., {h:19,m:30}]
const SLOTS = Array.from({ length: 24 }, (_, i) => ({ h: 8 + Math.floor(i / 2), m: (i % 2) * 30 }));
const DAY_NAMES = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const MONTH_NAMES = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

function pad(n) { return String(n).padStart(2, "0"); }
function ymd(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function slotHhmm(s) { return `${pad(s.h)}:${pad(s.m)}`; }
function slotEndHhmm(s) {
  const total = s.h * 60 + s.m + 30;
  return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
}

function getMonthMatrix(year, month) {
  // Returns array of ISO dates for the entire visible calendar (Mon-Sun weeks)
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startOffset = (first.getDay() + 6) % 7; // 0=Mon
  const days = [];
  // Prev month tail
  for (let i = startOffset; i > 0; i--) {
    const d = new Date(year, month, 1 - i);
    days.push({ date: d, ymd: ymd(d), inMonth: false });
  }
  // Current month
  for (let d = 1; d <= last.getDate(); d++) {
    const dt = new Date(year, month, d);
    days.push({ date: dt, ymd: ymd(dt), inMonth: true });
  }
  // Next month head to complete last week
  while (days.length % 7 !== 0) {
    const dt = new Date(days[days.length - 1].date);
    dt.setDate(dt.getDate() + 1);
    days.push({ date: dt, ymd: ymd(dt), inMonth: false });
  }
  // Group by weeks
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  return weeks;
}

export default function CalendarPage() {
  const { user } = useAuth();
  const now = new Date();
  const [cursor, setCursor] = useState({ year: now.getFullYear(), month: now.getMonth() });
  const [slots, setSlots] = useState({}); // key "ymd_hour_minute" -> available bool
  const [dirty, setDirty] = useState({}); // slots pending save
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(ymd(now));
  const [bookings, setBookings] = useState({}); // key "ymd_hour_minute" -> booking obj
  const [bookingDialog, setBookingDialog] = useState(null); // booking obj when clicked

  const weeks = useMemo(() => getMonthMatrix(cursor.year, cursor.month), [cursor]);

  // Reminder date: 10 days before day 1 of next month
  const reminderDate = useMemo(() => {
    const next1 = new Date(cursor.year, cursor.month + 1, 1);
    const d = new Date(next1);
    d.setDate(d.getDate() - 10);
    return d;
  }, [cursor]);

  const openNextMonthDate = reminderDate;
  const isNextMonthOpen = new Date() >= openNextMonthDate;

  const load = async () => {
    try {
      const [avail, book] = await Promise.all([
        api.get("/calendar/availability", { params: { year: cursor.year, month: cursor.month + 1 } }),
        api.get("/calendar/my-bookings", { params: { year: cursor.year, month: cursor.month + 1 } }).catch(() => ({ data: { bookings: [] } })),
      ]);
      const map = {};
      avail.data.forEach(s => {
        const mm = s.minute == null ? 0 : Number(s.minute);
        map[`${s.date}_${s.hour}_${mm}`] = s.available;
        if (s.minute == null) {
          map[`${s.date}_${s.hour}_30`] = s.available;
        }
      });
      setSlots(map);
      // Costruisci mappa prenotazioni: key `{date}_{hour}_{minute}` → booking object.
      // Include TUTTI gli slot da 30-min coperti dalla duration_minutes della prenotazione.
      const bmap = {};
      (book.data?.bookings || []).forEach(b => {
        const slotsNeeded = Math.ceil((b.duration_minutes || 30) / 30);
        const startMin = b.hour * 60 + (b.minute || 0);
        for (let k = 0; k < slotsNeeded; k++) {
          const total = startMin + k * 30;
          const h = Math.floor(total / 60);
          const m = total % 60;
          bmap[`${b.date}_${h}_${m}`] = { ...b, is_start: k === 0 };
        }
      });
      setBookings(bmap);
      setDirty({});
    } catch (err) { toast.error("Errore caricamento"); }
  };

  useEffect(() => { load(); }, [cursor.year, cursor.month]);

  const key = (day, h, m) => `${day}_${h}_${m}`;
  const isAvailable = (day, h, m) => {
    const k = key(day, h, m);
    return dirty[k] !== undefined ? dirty[k] : slots[k];
  };
  const setSlot = (day, h, m, val) => setDirty(d => ({ ...d, [key(day, h, m)]: val }));

  const toggleSlot = (day, h, m) => {
    const curr = isAvailable(day, h, m) || false;
    setSlot(day, h, m, !curr);
  };

  const toggleDay = (day) => {
    // Se almeno uno slot NON è attivo → attiva tutti; altrimenti disattiva tutti
    const anyOff = SLOTS.some(s => !isAvailable(day, s.h, s.m));
    const target = anyOff;
    const patch = {};
    SLOTS.forEach(s => { patch[key(day, s.h, s.m)] = target; });
    setDirty(d => ({ ...d, ...patch }));
  };

  const toggleWeek = (week) => {
    const days = week.filter(d => d.inMonth);
    if (!days.length) return;
    const total = days.length * SLOTS.length;
    let onCount = 0;
    days.forEach(d => SLOTS.forEach(s => { if (isAvailable(d.ymd, s.h, s.m)) onCount++; }));
    const target = onCount < total;
    const patch = {};
    days.forEach(d => SLOTS.forEach(s => { patch[key(d.ymd, s.h, s.m)] = target; }));
    setDirty(d => ({ ...d, ...patch }));
  };

  const dayFillPct = (day) => {
    const on = SLOTS.filter(s => isAvailable(day, s.h, s.m)).length;
    return on / SLOTS.length;
  };

  const saveAll = async () => {
    const payload = Object.entries(dirty).map(([k, v]) => {
      const [date, hStr, mStr] = k.split("_");
      return { date, hour: parseInt(hStr), minute: parseInt(mStr), available: v };
    });
    if (payload.length === 0) return toast.info("Nessuna modifica da salvare");
    setSaving(true);
    try {
      await api.post("/calendar/availability", { slots: payload });
      toast.success(`Salvate ${payload.length} disponibilità`);
      setDirty({});
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const { appleOpen, setAppleOpen, connectGoogle, disconnectGoogle, disconnectApple, refreshMe } = useCalendarConnections();

  const prevMonth = () => setCursor(c => ({ year: c.month === 0 ? c.year - 1 : c.year, month: c.month === 0 ? 11 : c.month - 1 }));
  const nextMonth = () => setCursor(c => ({ year: c.month === 11 ? c.year + 1 : c.year, month: c.month === 11 ? 0 : c.month + 1 }));

  const canEditMonth = user.role === "site_operator" || user.role === "admin";
  const pendingCount = Object.keys(dirty).length;

  return (
    <div>
      <OperatorPageTip
        pageKey="calendar"
        title="Il tuo calendario disponibilità"
        body="Blocca gli orari in cui NON sei disponibile per i sopralluoghi. Gli slot rimasti liberi verranno proposti automaticamente al cliente durante la prenotazione."
      />
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">{user.role === "admin" ? "Admin" : "Site Operator"}</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Calendario disponibilità</h1>
          <p className="text-muted-foreground mt-2">Definisci gli orari in cui sei disponibile per i sopralluoghi</p>
        </div>
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 w-full sm:w-auto">
          {(user.role === "professional" || user.role === "site_operator" || user.role === "admin") && (
            !user.google_calendar_connected ? (
              <Button variant="outline" onClick={connectGoogle} data-testid="connect-google" className="w-full sm:w-auto min-w-0">
                <GoogleLogo size={16} className="mr-2 shrink-0" weight="bold" />
                <span className="truncate">Collega Google Calendar</span>
              </Button>
            ) : (
              <div className="flex items-center gap-2 w-full sm:w-auto min-w-0">
                <div className="flex items-center gap-2 text-sm text-green-700 px-3 py-2 rounded-md bg-green-50 border border-green-200 min-w-0 flex-1 sm:flex-initial">
                  <CheckCircle size={16} weight="fill" className="shrink-0" />
                  <span className="truncate">Google Calendar</span>
                </div>
                <Button variant="outline" size="sm" onClick={disconnectGoogle} data-testid="disconnect-google" className="shrink-0">
                  Disconnetti
                </Button>
              </div>
            )
          )}
          {(user.role === "professional" || user.role === "site_operator" || user.role === "admin") && (
            !user.apple_calendar_connected ? (
              <Button variant="outline" onClick={() => setAppleOpen(true)} data-testid="connect-apple" className="w-full sm:w-auto min-w-0">
                <AppleLogo size={16} className="mr-2 shrink-0" weight="fill" />
                <span className="truncate">Collega Apple Calendar</span>
              </Button>
            ) : (
              <div className="flex items-center gap-2 w-full sm:w-auto min-w-0">
                <div className="flex items-center gap-2 text-sm text-slate-700 px-3 py-2 rounded-md bg-slate-100 border border-slate-300 min-w-0 flex-1 sm:flex-initial">
                  <CheckCircle size={16} weight="fill" className="shrink-0" />
                  <span className="truncate">Apple Calendar</span>
                </div>
                <Button variant="outline" size="sm" onClick={disconnectApple} data-testid="disconnect-apple" className="shrink-0">
                  Disconnetti
                </Button>
              </div>
            )
          )}
          <Button onClick={saveAll} disabled={saving || pendingCount === 0} className="ht-gradient border-0 w-full sm:w-auto" data-testid="save-availability">
            <FloppyDisk size={16} className="mr-2" weight="bold" />
            {saving ? "Salvataggio..." : `Salva${pendingCount > 0 ? ` (${pendingCount})` : ""}`}
          </Button>
        </div>
      </div>

      {/* Reminder banner */}
      <div className="kpi-card border-l-4 border-l-purple-500 mb-4 flex items-start gap-3">
        <Info size={20} color="#6D28D9" weight="fill" className="mt-0.5 shrink-0" />
        <div className="text-sm">
          <div className="font-semibold">Apertura mese successivo</div>
          <p className="text-muted-foreground">
            Il calendario per il mese successivo si apre 10 giorni prima del giorno 1.
            {" "}Prossima apertura: <strong>{openNextMonthDate.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}</strong>.
            {isNextMonthOpen ? " Aperto ✓" : " Chiuso"} — riceverai una mail di promemoria.
          </p>
        </div>
      </div>

      {/* Month header */}
      <div className="kpi-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Button size="sm" variant="outline" onClick={prevMonth} data-testid="cal-prev"><CaretLeft size={14} /></Button>
            <h2 className="font-display text-2xl font-semibold tracking-tight min-w-[220px] text-center">
              {MONTH_NAMES[cursor.month]} {cursor.year}
            </h2>
            <Button size="sm" variant="outline" onClick={nextMonth} data-testid="cal-next"><CaretRight size={14} /></Button>
          </div>
          <div className="text-xs text-muted-foreground hidden md:block">
            Clicca un giorno per aprire lo slot orario
          </div>
        </div>

        {/* Legenda visiva: spiega gli stati attivi */}
        <div className="mb-4 rounded-lg border bg-slate-50/60 p-3" data-testid="calendar-legend">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">Legenda</div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4 text-xs">
            {/* Settimana attiva */}
            <div className="flex items-start gap-2">
              <div className="w-8 h-10 rounded-md bg-purple-100 border border-purple-300 flex items-center justify-center text-[10px] font-bold text-purple-700 shrink-0">
                S1
              </div>
              <div>
                <div className="font-semibold text-slate-700">Settimana attiva</div>
                <div className="text-muted-foreground text-[11px]">
                  Clicca su <code className="bg-white px-1 rounded">S1…S6</code> per attivare/disattivare tutti i giorni della riga.
                </div>
              </div>
            </div>
            {/* Giorno attivo */}
            <div className="flex items-start gap-2">
              <div className="w-12 h-10 rounded-md bg-white border relative shrink-0">
                <div className="text-[10px] font-semibold ml-1 mt-0.5">15</div>
                <div className="absolute top-0.5 right-1 text-[8px] font-mono text-muted-foreground">12h</div>
                <div className="absolute bottom-1 left-1 right-1 h-1 rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-full ht-gradient" style={{ width: "60%" }} />
                </div>
              </div>
              <div>
                <div className="font-semibold text-slate-700">Giorno con slot attivi</div>
                <div className="text-muted-foreground text-[11px]">
                  La barra viola indica la % di ore prenotabili; il badge <code className="bg-white px-1 rounded">Nh</code> il totale.
                </div>
              </div>
            </div>
            {/* Ora attiva */}
            <div className="flex items-start gap-2">
              <div className="w-12 h-10 rounded-md ht-gradient border-0 shadow-sm flex flex-col items-center justify-center text-white shrink-0">
                <div className="font-display font-bold text-[10px]">09:00</div>
                <div className="text-[8px] opacity-80">— 10:00</div>
              </div>
              <div>
                <div className="font-semibold text-slate-700">Ora attiva</div>
                <div className="text-muted-foreground text-[11px]">
                  Slot viola pieno = prenotabile. Clicca sullo slot bianco per attivarlo.
                </div>
              </div>
            </div>
            {/* Non disponibile */}
            <div className="flex items-start gap-2">
              <div className="w-12 h-10 rounded-md bg-white border flex flex-col items-center justify-center text-muted-foreground shrink-0">
                <div className="font-display font-bold text-[10px]">14:00</div>
                <div className="text-[8px]">— 15:00</div>
              </div>
              <div>
                <div className="font-semibold text-slate-700">Non disponibile</div>
                <div className="text-muted-foreground text-[11px]">
                  Slot vuoto = non prenotabile dai professionisti.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Month grid with day fill preview */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {DAY_NAMES.map(d => <div key={d} className="text-center text-xs font-semibold text-muted-foreground py-1">{d}</div>)}
        </div>
        <div className="space-y-1">
          {weeks.map((week, wi) => (
            <div key={wi} className="flex gap-1 items-stretch">
              <button onClick={() => toggleWeek(week)} disabled={!canEditMonth}
                className="w-8 shrink-0 rounded-md border bg-[color:hsl(var(--surface-alt))] hover:bg-purple-100 text-xs font-semibold text-muted-foreground"
                data-testid={`week-toggle-${wi}`}
                title="Attiva/disattiva intera settimana">
                S{wi + 1}
              </button>
              <div className="grid grid-cols-7 gap-1 flex-1">
                {week.map(day => {
                  const pct = day.inMonth ? dayFillPct(day.ymd) : 0;
                  const isToday = day.ymd === ymd(new Date());
                  const isSelected = day.ymd === selectedDate;
                  return (
                    <button key={day.ymd}
                      onClick={() => setSelectedDate(day.ymd)}
                      disabled={!day.inMonth}
                      className={`relative h-16 rounded-md border text-left px-2 py-1 transition-all ${
                        !day.inMonth ? "opacity-30 cursor-not-allowed" :
                        isSelected ? "border-purple-600 ring-2 ring-purple-200 shadow-sm" :
                        "hover:border-purple-400"
                      } ${isToday ? "bg-purple-50" : "bg-white"}`}
                      data-testid={`day-${day.ymd}`}>
                      <div className={`text-sm font-semibold ${isToday ? "text-purple-700" : ""}`}>{day.date.getDate()}</div>
                      {day.inMonth && (
                        <>
                          <div className="absolute bottom-1 left-1 right-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full ht-gradient border-0 transition-all" style={{ width: `${pct * 100}%` }} />
                          </div>
                          <div className="absolute top-1 right-1 text-[10px] font-mono text-muted-foreground">
                            {Math.round(pct * SLOTS.length * 0.5)}h
                          </div>
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Hourly editor for selected day */}
      <div className="kpi-card mt-4" data-testid="day-editor">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-eyebrow">Giorno selezionato</div>
            <h3 className="font-display text-xl font-semibold mt-1">
              {new Date(selectedDate).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </h3>
          </div>
          <Button size="sm" variant="outline" onClick={() => toggleDay(selectedDate)} disabled={!canEditMonth} data-testid="toggle-day">
            <CalendarIcon size={14} className="mr-1" /> Attiva/disattiva intera giornata
          </Button>
        </div>
        <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-12 gap-2">
          {SLOTS.map(s => {
            const on = isAvailable(selectedDate, s.h, s.m);
            const skey = `${s.h}_${s.m}`;
            const booking = bookings[`${selectedDate}_${s.h}_${s.m}`];
            if (booking) {
              // Slot occupato da una prenotazione: click apre dialog con opzione revoca
              return (
                <button key={skey}
                  onClick={() => setBookingDialog(booking)}
                  className="h-16 rounded-md border-2 border-amber-500 bg-amber-100 hover:bg-amber-200 flex flex-col items-center justify-center text-xs font-semibold transition-all"
                  data-testid={`booked-slot-${selectedDate}-${s.h}-${s.m}`}
                  title={`${booking.service_name} — ${booking.professional_name}`}>
                  <div className="font-display font-bold text-amber-900">{slotHhmm(s)}</div>
                  <div className="text-[9px] text-amber-800 truncate max-w-full px-1">
                    {booking.is_start ? `🔒 ${booking.service_name}` : "↑ occupato"}
                  </div>
                </button>
              );
            }
            return (
              <button key={skey}
                onClick={() => toggleSlot(selectedDate, s.h, s.m)}
                disabled={!canEditMonth}
                className={`h-16 rounded-md border flex flex-col items-center justify-center text-sm font-medium transition-all ${
                  on ? "ht-gradient border-0 text-white shadow-md" : "bg-white hover:border-purple-400 text-muted-foreground"
                }`}
                data-testid={`slot-${selectedDate}-${s.h}-${s.m}`}>
                <div className="font-display font-bold">{slotHhmm(s)}</div>
                <div className="text-[10px] opacity-80">— {slotEndHhmm(s)}</div>
              </button>
            );
          })}
        </div>
      </div>

      {user.role === "admin" && <AdminAvailabilityOverview />}

      <BookingDetailDialog booking={bookingDialog} onClose={() => setBookingDialog(null)} onDone={load} />

      <AppleConnectDialog open={appleOpen} onOpenChange={setAppleOpen} onConnected={refreshMe} />
    </div>
  );
}

// ==================== BOOKING DETAIL DIALOG ====================
// Aperto quando il site operator clicca su uno slot occupato. Mostra i dettagli
// della prenotazione e permette di richiedere la revoca del servizio con motivo.
function BookingDetailDialog({ booking, onClose, onDone }) {
  const nav = useNavigate();
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const open = !!booking;

  useEffect(() => { if (open) setReason(""); }, [booking?.practice_id, open]);

  if (!booking) return null;

  const doRevoke = async () => {
    if (reason.trim().length < 10) {
      toast.error("Motivo obbligatorio (minimo 10 caratteri)");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/practices/${booking.practice_id}/reject`, { reason: reason.trim() });
      toast.success("Richiesta di revoca inviata. Un admin la valuterà.");
      onDone && onDone();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore invio revoca");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            🔒 Prenotazione dello slot
          </DialogTitle>
          <DialogDescription>
            Dettagli della prenotazione. Puoi richiedere la revoca se non riesci a coprire il sopralluogo.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm py-1">
          <div className="rounded border bg-slate-50 p-3 space-y-1">
            <div><span className="text-slate-500 text-[11px] uppercase tracking-widest">Servizio</span><div className="font-semibold">{booking.service_name}</div></div>
            <div><span className="text-slate-500 text-[11px] uppercase tracking-widest">Immobile</span><div>{booking.property_address || "—"}</div></div>
            <div><span className="text-slate-500 text-[11px] uppercase tracking-widest">Data e ora</span><div className="font-mono">{booking.date} · {String(booking.hour).padStart(2,"0")}:{String(booking.minute).padStart(2,"0")} · {booking.duration_minutes} min</div></div>
            <div><span className="text-slate-500 text-[11px] uppercase tracking-widest">Professionista</span><div>{booking.professional_name}</div></div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Motivo revoca (min 10 caratteri)</label>
            <Textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Es. Non riesco a coprire questo sopralluogo, ho un impegno urgente."
              data-testid="calendar-revoke-reason"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => nav(`/board?highlight=${booking.practice_id}`)} data-testid="calendar-open-board">
            Vai alla bacheca
          </Button>
          <Button variant="outline" onClick={() => nav(`/practices/${booking.practice_id}`)} data-testid="calendar-open-practice">
            Apri pratica
          </Button>
          <Button variant="ghost" onClick={onClose}>Chiudi</Button>
          <Button
            className="bg-red-600 hover:bg-red-700 text-white"
            onClick={doRevoke}
            disabled={submitting || reason.trim().length < 10}
            data-testid="calendar-revoke-confirm"
          >
            {submitting ? "Invio…" : "Richiedi revoca"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

